import React, { useState } from 'react';
import { Alert, Card, Collapse, Badge, Space, Button, Tooltip, Tag, Progress, List, Typography, Divider } from 'antd';
import { 
  WarningOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import useAutomationInsights from '../hooks/useAutomationInsights';
import QuickTaskGenerator from './QuickTaskGenerator';

const { Panel } = Collapse;
const { Text, Title } = Typography;

const SmartAutomationAlerts = ({ incidentId, incidentData }) => {
  const [refreshing, setRefreshing] = useState(false);

  // Use the automation insights hook with real-time updates
  const {
    insights,
    loading,
    error,
    refresh,
    lastRefresh,
    hasIssues,
    hasCriticalIssues,
    totalIssues,
    criticalIssuesCount
  } = useAutomationInsights(incidentId, incidentData, {
    refreshInterval: 30000, // 30 seconds
    enableRealTimeUpdates: true,
    autoRefreshOnTaskChanges: true
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refresh();
    setRefreshing(false);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#faad14';
      default: return '#52c41a';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'high': return <WarningOutlined style={{ color: '#fa8c16' }} />;
      case 'medium': return <InfoCircleOutlined style={{ color: '#faad14' }} />;
      default: return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
  };

  if (loading) {
    return (
      <Card 
        title="🤖 Smart Automation Insights" 
        loading={true}
        style={{ marginBottom: '16px' }}
      >
        Analyzing tasks, deadlines, and workload...
      </Card>
    );
  }

  if (error) {
    return (
      <Card 
        title="🤖 Smart Automation Insights" 
        style={{ marginBottom: '16px' }}
      >
        <Alert
          message="Error Loading Automation Insights"
          description={error}
          type="error"
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      </Card>
    );
  }

  if (!insights) {
    return null;
  }

  const { missingTasks, deadlines, workload } = insights;

  return (
    <Card 
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <span>🤖 Smart Automation Insights</span>
            {hasIssues && (
              <Badge 
                count={totalIssues} 
                style={{ backgroundColor: criticalIssuesCount > 0 ? '#ff4d4f' : '#fa8c16' }}
              />
            )}
            {lastRefresh && (
              <Tooltip title={`Last updated: ${lastRefresh.toLocaleTimeString()}`}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {lastRefresh.toLocaleTimeString()}
                </Text>
              </Tooltip>
            )}
          </Space>
          <Button 
            type="text" 
            size="small" 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
        </div>
      }
      style={{ marginBottom: '16px' }}
    >
      {!hasIssues ? (
        <Alert
          message="All Systems Running Smoothly"
          description="No critical issues detected. Your incident response is well-organized."
          type="success"
          icon={<CheckCircleOutlined />}
          showIcon
        />
      ) : (
        <Collapse 
          ghost 
          defaultActiveKey={criticalIssuesCount > 0 ? ['missing', 'deadlines', 'workload'] : []}
        >
          {/* MISSING TASKS ANALYSIS */}
          <Panel 
            header={
              <Space>
                <PlusOutlined style={{ color: '#1890ff' }} />
                <span>Missing Task Detection</span>
                {(missingTasks.missingCriticalTasks?.length > 0 || missingTasks.missingRecommendedTasks?.length > 0) && (
                  <Badge 
                    count={missingTasks.missingCriticalTasks?.length + missingTasks.missingRecommendedTasks?.length} 
                    style={{ backgroundColor: '#1890ff' }}
                  />
                )}
                <Text type="secondary">
                  {missingTasks.coverage}% task coverage
                </Text>
              </Space>
            }
            key="missing"
          >
            <div style={{ marginBottom: '16px' }}>
              <Progress 
                percent={missingTasks.coverage} 
                status={missingTasks.coverage >= 80 ? 'success' : missingTasks.coverage >= 60 ? 'normal' : 'exception'}
                format={percent => `${percent}% Complete`}
              />
            </div>

            {missingTasks.missingCriticalTasks?.length > 0 && (
              <>
                <Title level={5} style={{ color: '#ff4d4f', marginBottom: '8px' }}>
                  🚨 Missing Critical Tasks ({missingTasks.missingCriticalTasks.length})
                </Title>
                <List
                  size="small"
                  dataSource={missingTasks.missingCriticalTasks}
                  renderItem={task => (
                    <List.Item>
                      <Space>
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                        <Text strong>{task.name}</Text>
                        <Tag color="red">{task.assignedRole}</Tag>
                        <Text type="secondary">{task.description}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </>
            )}

            {missingTasks.missingRecommendedTasks?.length > 0 && (
              <>
                <Title level={5} style={{ color: '#fa8c16', marginBottom: '8px', marginTop: '16px' }}>
                  ⚠️ Missing Recommended Tasks ({missingTasks.missingRecommendedTasks.length})
                </Title>
                <List
                  size="small"
                  dataSource={missingTasks.missingRecommendedTasks}
                  renderItem={task => (
                    <List.Item>
                      <Space>
                        <WarningOutlined style={{ color: '#fa8c16' }} />
                        <Text>{task.name}</Text>
                        <Tag color="orange">{task.assignedRole}</Tag>
                        <Text type="secondary">{task.description}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </>
            )}

            {missingTasks.suggestions?.length > 0 && (
              <>
                <Divider />
                <Title level={5} style={{ marginBottom: '8px' }}>
                  💡 Smart Suggestions
                </Title>
                {missingTasks.suggestions.map((suggestion, index) => (
                  <Alert
                    key={index}
                    message={suggestion.message}
                    description={suggestion.action}
                    type={suggestion.priority === 'critical' ? 'error' : suggestion.priority === 'high' ? 'warning' : 'info'}
                    showIcon
                    style={{ marginBottom: '8px' }}
                  />
                ))}
              </>
            )}

            {(missingTasks.missingCriticalTasks?.length > 0 || missingTasks.missingRecommendedTasks?.length > 0) && (
              <>
                <Divider />
                <QuickTaskGenerator 
                  incidentId={incidentId}
                  missingTasks={[...missingTasks.missingCriticalTasks, ...missingTasks.missingRecommendedTasks]}
                  onTasksGenerated={(generatedTasks) => {
                    // Refresh insights after tasks are generated
                    setTimeout(() => {
                      refresh();
                    }, 1000);
                  }}
                />
              </>
            )}
          </Panel>

          {/* DEADLINE WARNINGS */}
          <Panel 
            header={
              <Space>
                <ClockCircleOutlined style={{ color: '#fa8c16' }} />
                <span>Deadline Monitoring</span>
                {deadlines.summary?.total > 0 && (
                  <Badge 
                    count={deadlines.summary.total} 
                    style={{ backgroundColor: deadlines.summary.overdue > 0 ? '#ff4d4f' : '#fa8c16' }}
                  />
                )}
              </Space>
            }
            key="deadlines"
          >
            {deadlines.warnings?.length === 0 ? (
              <Alert
                message="All Tasks On Track"
                description="No deadline concerns detected."
                type="success"
                showIcon
              />
            ) : (
              <List
                dataSource={deadlines.warnings}
                renderItem={warning => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={getSeverityIcon(warning.severity)}
                      title={
                        <Space>
                          <Text strong style={{ color: getSeverityColor(warning.severity) }}>
                            {warning.message}
                          </Text>
                          <Tag color={warning.type === 'overdue' ? 'red' : warning.type === 'critical_soon' ? 'orange' : 'blue'}>
                            {warning.type.replace('_', ' ').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Text>{warning.action}</Text>
                          <Space>
                            <Text type="secondary">Task: {warning.task.name}</Text>
                            <Text type="secondary">Owner: {warning.task.owner || 'Unassigned'}</Text>
                            <Text type="secondary">Priority: {warning.task.priority || 'Normal'}</Text>
                          </Space>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Panel>

          {/* WORKLOAD CONFLICTS */}
          <Panel 
            header={
              <Space>
                <UserOutlined style={{ color: '#722ed1' }} />
                <span>Workload Analysis</span>
                {workload.summary?.total > 0 && (
                  <Badge 
                    count={workload.summary.total} 
                    style={{ backgroundColor: workload.summary.critical > 0 ? '#ff4d4f' : '#722ed1' }}
                  />
                )}
              </Space>
            }
            key="workload"
          >
            {workload.conflicts?.length === 0 ? (
              <Alert
                message="Workload Well Balanced"
                description={`${workload.workloadStats?.totalActiveTasks || 0} active tasks distributed across ${workload.workloadStats?.ownersWithTasks || 0} team members.`}
                type="success"
                showIcon
              />
            ) : (
              <List
                dataSource={workload.conflicts}
                renderItem={conflict => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={getSeverityIcon(conflict.severity)}
                      title={
                        <Space>
                          <Text strong style={{ color: getSeverityColor(conflict.severity) }}>
                            {conflict.message}
                          </Text>
                          <Tag color={conflict.severity === 'critical' ? 'red' : conflict.severity === 'high' ? 'orange' : 'blue'}>
                            {conflict.type.replace('_', ' ').toUpperCase()}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small">
                          <Text>{conflict.action}</Text>
                          <Space>
                            {conflict.owner && <Text type="secondary">Owner: {conflict.owner}</Text>}
                            {conflict.role && <Text type="secondary">Role: {conflict.role}</Text>}
                            <Text type="secondary">Tasks: {conflict.taskCount}</Text>
                          </Space>
                          {conflict.tasks?.length > 0 && (
                            <div>
                              <Text type="secondary">Affected tasks: </Text>
                              {conflict.tasks.slice(0, 3).map(task => (
                                <Tag key={task.id} size="small">{task.name}</Tag>
                              ))}
                              {conflict.tasks.length > 3 && (
                                <Text type="secondary"> +{conflict.tasks.length - 3} more</Text>
                              )}
                            </div>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Panel>
        </Collapse>
      )}

      {hasIssues && (
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '6px' }}>
          <Text type="secondary">
            <InfoCircleOutlined /> These insights are automatically generated to help optimize your crisis response. 
            Issues are prioritized by impact and urgency. Updates in real-time as tasks change.
          </Text>
        </div>
      )}
    </Card>
  );
};

export default SmartAutomationAlerts; 